import { createApi } from '@reduxjs/toolkit/query/react';
import { authBaseQuery } from '../../app/authBaseQuery';
import { getRefreshToken } from '../../app/authTokens';

/**
 * Auth surface of the real Cavan IMS API
 * (https://cavan-investment-app-backend.onrender.com/api/documentation).
 * Most paths match the published OpenAPI doc exactly; three were corrected
 * per the backend team directly (the live routes differ from what's
 * documented) — see the per-endpoint comments below for which.
 * This is the only feature slice talking to the real backend right now —
 * every other resource area is still "not yet populated" per the docs, so
 * everything else in this app stays on the mock server (see
 * app/axiosBaseQuery.js).
 */
export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: authBaseQuery(),
  tagTypes: ['Me'],
  endpoints: (builder) => ({
    // POST /auth/register — always assigns the investor role. Does not
    // establish a session; the user verifies their email, then logs in.
    register: builder.mutation({
      query: (body) => ({ url: '/auth/register', method: 'POST', data: body }),
    }),

    // POST /auth/login — either establishes a session directly, or (for
    // admin/super-admin accounts) returns { requires2fa: true } and the
    // caller must follow up with verifyTwoFactor.
    login: builder.mutation({
      query: (body) => ({ url: '/auth/login', method: 'POST', data: body }),
    }),

    // POST /auth/login/verify-2fa — second step of admin/super-admin
    // login. Establishes a session exactly like a normal login.
    verifyTwoFactor: builder.mutation({
      query: (body) => ({ url: '/auth/login/verify-2fa', method: 'POST', data: body }),
      invalidatesTags: ['Me'],
    }),

    // POST /auth/magic-link/request — investor-only passwordless login
    // request. Always returns 200 regardless of whether the email is
    // registered. (Path confirmed with the backend team — differs from
    // the published OpenAPI doc, which shows /auth/magic-link.)
    requestMagicLink: builder.mutation({
      query: (body) => ({ url: '/auth/magic-link/request', method: 'POST', data: body }),
    }),

    // POST /auth/magic-link/consume — single-use; establishes a session.
    consumeMagicLink: builder.mutation({
      query: (body) => ({ url: '/auth/magic-link/consume', method: 'POST', data: body }),
      invalidatesTags: ['Me'],
    }),

    // POST /auth/refresh — rotates the refresh token, issues a new access
    // token. Called directly by authAxios's interceptor on a 401, and once
    // on app bootstrap (see ProtectedRoute) — rarely called directly from
    // a component.
    refresh: builder.mutation({
      query: (body) => ({ url: '/auth/refresh', method: 'POST', data: body }),
    }),

    // POST /auth/logout — revokes the current refresh token. Always sends
    // whatever refresh token we're holding explicitly in the body (see
    // authTokens.js for why) — callers don't need to pass it themselves.
    logout: builder.mutation({
      query: (body) => {
        const refresh_token = getRefreshToken();
        return { url: '/auth/logout', method: 'POST', data: { ...(refresh_token ? { refresh_token } : {}), ...body } };
      },
      invalidatesTags: ['Me'],
    }),

    // POST /auth/verify-email/otp — consumes the OTP emailed at
    // registration. (Path confirmed with the backend team — differs from
    // the published OpenAPI doc, which shows /auth/verify-email.)
    verifyEmail: builder.mutation({
      query: (body) => ({ url: '/auth/verify-email/otp', method: 'POST', data: body }),
    }),

    // POST /auth/verify-email/resend — always 200, enumeration-safe.
    resendVerifyEmail: builder.mutation({
      query: (body) => ({ url: '/auth/verify-email/resend', method: 'POST', data: body }),
    }),

    // POST /auth/change-password — requires current_password. Revokes
    // every other session and clears must_change_password on success.
    changePassword: builder.mutation({
      query: (body) => ({ url: '/auth/change-password', method: 'POST', data: body }),
      invalidatesTags: ['Me'],
    }),

    // POST /auth/forgot-password — always 200, enumeration-safe.
    forgotPassword: builder.mutation({
      query: (body) => ({ url: '/auth/forgot-password', method: 'POST', data: body }),
    }),

    // POST /auth/reset-password — { email, otp, password } in one call,
    // no separate "verify the code first" step on this API.
    resetPassword: builder.mutation({
      query: (body) => ({ url: '/auth/reset-password', method: 'POST', data: body }),
    }),

    // GET /auth/user — subject to the forced-password-change gate like any
    // other protected endpoint. (Path confirmed with the backend team —
    // differs from the published OpenAPI doc, which shows /auth/me.)
    me: builder.query({
      query: () => ({ url: '/auth/user', method: 'GET' }),
      providesTags: ['Me'],
    }),
  }),
});

export const {
  useRegisterMutation,
  useLoginMutation,
  useVerifyTwoFactorMutation,
  useRequestMagicLinkMutation,
  useConsumeMagicLinkMutation,
  useRefreshMutation,
  useLogoutMutation,
  useVerifyEmailMutation,
  useResendVerifyEmailMutation,
  useChangePasswordMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useMeQuery,
  useLazyMeQuery,
} = authApi;
